const MAP_CONSENT_KEY = 'htv:gmaps_consent';
const MAP_SCRIPT_SELECTOR = 'script[src*="maps.googleapis.com/maps/api/js"]';

const hasMapConsent = () => {
  try {
    return localStorage.getItem(MAP_CONSENT_KEY) === 'true'
      || sessionStorage.getItem(MAP_CONSENT_KEY) === 'true'
      || localStorage.getItem('htv_map_consent') === '1'
      || localStorage.getItem('htv_map_consent') === 'true';
  } catch {
    return false;
  }
};

const rememberMapConsent = () => {
  try {
    localStorage.setItem(MAP_CONSENT_KEY, 'true');
  } catch {
    return;
  }
};

const waitForExistingMapsScript = () => new Promise((resolve, reject) => {
  let attempts = 0;
  const timer = window.setInterval(() => {
    attempts += 1;

    if (window.google?.maps) {
      window.clearInterval(timer);
      resolve();
      return;
    }

    if (attempts >= 150) {
      window.clearInterval(timer);
      reject(new Error('Google Maps did not finish loading'));
    }
  }, 100);
});

const loadGoogleMaps = (apiKey) => {
  if (window.google?.maps) return Promise.resolve();
  if (window.__nlGoogleMapsPromise) return window.__nlGoogleMapsPromise;

  if (document.querySelector(MAP_SCRIPT_SELECTOR)) {
    window.__nlGoogleMapsPromise = waitForExistingMapsScript();
    return window.__nlGoogleMapsPromise;
  }

  window.__nlGoogleMapsPromise = new Promise((resolve, reject) => {
    const callbackName = `__nlCityMapsCallback_${Date.now()}`;
    window[callbackName] = () => {
      delete window[callbackName];
      resolve();
    };

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&callback=${callbackName}&v=weekly`;
    script.async = true;
    script.onerror = () => {
      delete window[callbackName];
      window.__nlGoogleMapsPromise = null;
      reject(new Error('Google Maps failed to load'));
    };
    document.head.appendChild(script);
  });

  return window.__nlGoogleMapsPromise;
};

class CityPharmacyMap extends HTMLElement {
  connectedCallback() {
    this.cards = [...this.querySelectorAll('[data-pharmacy-card]')];
    this.filters = [...this.querySelectorAll('[data-city-filter]')];
    this.citySections = [...this.querySelectorAll('[data-city-section]')];
    this.mapElement = this.querySelector('[data-city-map]');
    this.consent = this.querySelector('[data-map-consent]');
    this.consentButton = this.querySelector('[data-map-consent-button]');
    this.error = this.querySelector('[data-map-error]');

    this.filters.forEach((button) => {
      button.addEventListener('click', () => this.#filter(button.dataset.cityFilter));
    });
    this.cards.forEach((card) => {
      card.addEventListener('click', (event) => {
        if (event.target.closest('a, button')) return;
        this.#focusCard(card);
      });
    });
    this.consentButton?.addEventListener('click', () => {
      rememberMapConsent();
      this.#initializeMap();
    });

    const defaultCity = this.dataset.defaultCity || this.filters[0]?.dataset.cityFilter;
    if (defaultCity) this.#filter(defaultCity);

    if (hasMapConsent()) this.#initializeMap();
  }

  async #initializeMap() {
    if (this.map || !this.mapElement) return;

    const apiKey = window.__HTV_CONFIG__?.googleMapsApiKey;
    if (!apiKey) {
      this.#showError();
      return;
    }

    try {
      await loadGoogleMaps(apiKey);
      this.consent?.setAttribute('hidden', '');
      this.mapElement.removeAttribute('aria-hidden');

      this.map = new google.maps.Map(this.mapElement, {
        center: { lat: 51.1657, lng: 10.4515 },
        zoom: 6,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
      });

      this.markers = this.cards.map((card) => {
        const position = {
          lat: Number(card.dataset.latitude),
          lng: Number(card.dataset.longitude),
        };
        if (!Number.isFinite(position.lat) || !Number.isFinite(position.lng)) return null;

        const marker = new google.maps.Marker({
          map: this.map,
          position,
          title: card.dataset.pharmacyName,
        });
        marker.addListener('click', () => {
          const sheetTrigger = card.querySelector('[data-sheet-trigger]');
          if (sheetTrigger) {
            sheetTrigger.click();
            return;
          }
          card.scrollIntoView({ behavior: 'smooth', block: 'center' });
          card.focus({ preventScroll: true });
        });

        return { card, marker, position };
      }).filter(Boolean);

      this.#fitVisibleMarkers();
    } catch {
      this.#showError();
    }
  }

  #filter(city) {
    this.filters.forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.cityFilter === city));
    });
    this.cards.forEach((card) => {
      card.hidden = city !== 'all' && card.dataset.city !== city;
    });
    this.citySections.forEach((section) => {
      section.hidden = city !== 'all' && section.dataset.city !== city;
    });
    this.#toggleCityProductRows(city);
    this.#fitVisibleMarkers();
  }

  // Produktreihen unter dem Widget (featured-collection mit der Abhol-Collection einer Stadt)
  // folgen der Stadt-Auswahl (Lilli, 06.10.2026). Zuordnung über die Collection-URL des
  // Stadt-Blocks, keine eigene Einstellung nötig. Nur aktiv, wenn es Stadt-Chips gibt.
  #toggleCityProductRows(city) {
    if (!this.filters.length) return;
    const own = this.closest('.shopify-section');
    this.querySelectorAll('template[data-city-pool]').forEach((pool) => {
      const url = pool.dataset.collectionUrl;
      if (!url) return;
      document.querySelectorAll('.shopify-section').forEach((section) => {
        if (section === own || !section.querySelector('.product-list')) return;
        if (!section.querySelector(`a[href="${url}"]`)) return;
        section.style.display = city === 'all' || pool.dataset.cityPool === city ? '' : 'none';
      });
    });
  }

  #fitVisibleMarkers() {
    if (!this.map || !this.markers) return;

    const bounds = new google.maps.LatLngBounds();
    let visibleCount = 0;
    for (const item of this.markers) {
      const visible = !item.card.hidden;
      item.marker.setMap(visible ? this.map : null);
      if (!visible) continue;
      bounds.extend(item.position);
      visibleCount += 1;
    }

    if (visibleCount > 0) this.map.fitBounds(bounds, 56);
  }

  #focusCard(card) {
    if (!this.map || !this.markers) return;
    const item = this.markers.find((entry) => entry.card === card);
    if (!item) return;
    this.map.panTo(item.position);
    this.map.setZoom(14);
  }

  #showError() {
    this.consent?.setAttribute('hidden', '');
    this.error?.removeAttribute('hidden');
  }
}

if (!customElements.get('city-pharmacy-map')) {
  customElements.define('city-pharmacy-map', CityPharmacyMap);
}

// Kompakt-Layout (Abholung): Apothekenzeile öffnet ein gemeinsames Fenster mit den dort
// abholbaren Produkten. Die Karten liegen im <template data-city-pool> und tragen ihre
// Abholapotheken (PharmOps `custom.pickup_pharmacies`) in data-pickup-pharmacies.
class CityPharmacySheet {
  constructor(hub) {
    this.hub = hub;
    this.drawer = hub.querySelector('.city-pharmacy-hub__sheet');
    if (!this.drawer) return;
    this.title = this.drawer.querySelector('[data-sheet-title]');
    this.address = this.drawer.querySelector('[data-sheet-address]');
    this.status = this.drawer.querySelector('[data-sheet-status]');
    this.directions = this.drawer.querySelector('[data-sheet-directions] a');
    this.productsWrapper = this.drawer.querySelector('[data-sheet-products-wrapper]');
    this.products = this.drawer.querySelector('[data-sheet-products]');
    this.allLink = this.drawer.querySelector('[data-sheet-all-link]');
    hub.querySelectorAll('[data-sheet-trigger]').forEach((button) => {
      button.addEventListener('click', () => this.fill(button.closest('[data-pharmacy-card]')));
    });
    // Hinzufügen im Fenster = Abholung in dieser Apotheke. PharmOps' öffentliches Event setzt die
    // Abholapotheke; beim cart/add injiziert der Tracker `_pharmacy_pickup` + `_fulfillment_method`.
    this.products.addEventListener('click', () => {
      if (!this.currentHandle) return;
      document.dispatchEvent(new CustomEvent('htv:pharmacy-pickup-changed', { detail: { handle: this.currentHandle } }));
    }, true);
    this.#hydrateRows();
  }

  // Öffnungsstatus in den Zeilen; wartet auf den PharmOps-Renderer (App-Embed, lädt erst
  // nach mehreren Sekunden – gemessen 05.10.2026 > 6 s), bis zu 60 s, danach minütlich
  #hydrateRows(attempt = 0) {
    if (!window.HTV_OPENING_STATUS) {
      if (attempt < 120) window.setTimeout(() => this.#hydrateRows(attempt + 1), 500);
      return;
    }
    this.hub.querySelectorAll('[data-pharmacy-card]').forEach((row) => {
      const target = row.querySelector('.cart-pharmacy-modal__status');
      if (target) this.#renderStatus(target, row.dataset.openingHoursJson, true);
    });
    window.setTimeout(() => this.#hydrateRows(), 60 * 1000);
  }

  fill(row) {
    if (!row) return;
    const handle = row.dataset.pharmacyHandle;
    this.title.textContent = row.dataset.pharmacyName || '';
    this.address.textContent = row.dataset.pharmacyAddress || '';
    this.address.hidden = !row.dataset.pharmacyAddress;
    this.#renderStatus(this.status, row.dataset.openingHoursJson);
    if (this.directions) this.directions.href = row.dataset.directions || '#';

    const pool = this.hub.querySelector(`template[data-city-pool="${row.dataset.city}"]`);
    const needle = `|${handle}|`;
    const items = pool
      ? [...pool.content.querySelectorAll('[data-pickup-pharmacies]')].filter((item) => item.dataset.pickupPharmacies.includes(needle))
      : [];
    const cards = items.map((item) => item.firstElementChild.cloneNode(true));
    // PDP-Links tragen den Abhol-Filter: PharmOps (pharmacy-tracker.js) liest ihn und wählt die Apotheke vor
    const pickupParam = `filter.v.m.custom.pickup_pharmacies=${encodeURIComponent(handle)}`;
    cards.forEach((card) => card.querySelectorAll('a[href*="/products/"]').forEach((link) => {
      const url = new URL(link.getAttribute('href'), window.location.origin);
      url.searchParams.set('filter.v.m.custom.pickup_pharmacies', handle);
      link.setAttribute('href', url.pathname + url.search);
    }));
    this.products.replaceChildren(...cards);
    this.currentHandle = handle;
    this.productsWrapper.hidden = items.length === 0;
    if (this.allLink && pool) {
      this.allLink.href = `${pool.dataset.collectionUrl}?${pickupParam}`;
    }
  }

  // short = Zeilen-Kurzform (Lilli, 05.10.2026): geöffnet → „heute bis 22:30“, sonst der PharmOps-Satz
  #renderStatus(element, raw, short = false) {
    element.textContent = '';
    element.removeAttribute('data-state');
    const renderer = window.HTV_OPENING_STATUS;
    if (!raw || !renderer || typeof renderer.render !== 'function') return;
    let data = null;
    try { data = JSON.parse(raw); } catch { return; }
    const locale = (document.documentElement.lang || '').toLowerCase().startsWith('en') ? 'en' : 'de';
    const result = renderer.render(data, new Date(), locale);
    const isOpen = result.state === 'open' || result.state === 'closing-soon';
    element.textContent = short && isOpen && result.openUntil ? `heute bis ${result.openUntil}` : (result.message || '');
    if (result.state) element.setAttribute('data-state', result.state);
  }
}

document.querySelectorAll('city-pharmacy-map.city-pharmacy-hub--sheet').forEach((hub) => new CityPharmacySheet(hub));
document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('city-pharmacy-map.city-pharmacy-hub--sheet').forEach((hub) => new CityPharmacySheet(hub));
});
